/**
 * ESC Version Parser for Firefleet Frontend
 * Mirrors the parsing logic from pilot frontend DroneTable.js
 */

const ESC_RESULTS = ['UNKNOWN', 'SUCCESS', 'NO_DATA', 'MISMATCH', 'NEED_UPDATE', 'DAMAGED'];
const ESC_TYPES = ['UNKNOWN', 'AM32', 'BLHELI', 'AM32', 'BLUEJAY'];

/**
 * Parse ESC version data into a display-friendly format
 * @param {number|null} result - ESC version result (0-5)
 * @param {number|null} type - ESC type (0-4)
 * @param {number|null} major - Major version number
 * @param {number|null} minor - Minor version number
 * @returns {Object} - { display: string, status: 'ok'|'warning'|'error'|'unknown' }
 */
export function parseESCVersion(result, type, major, minor) {
  // Handle null/undefined/default values (255 is the default "no data" value)
  if (result === null || result === undefined || result === 255) {
    return { display: '-', status: 'unknown' };
  }

  const resultStr = ESC_RESULTS[result] ?? 'UNKNOWN';
  const typeStr = ESC_TYPES[type] ?? 'UNKNOWN';

  switch (resultStr) {
    case 'NO_DATA':
      return { display: 'No Data', status: 'error' };
    case 'MISMATCH':
      return { display: 'Mismatch Detected', status: 'error' };
    case 'DAMAGED':
      return { display: 'Damage Detected', status: 'error' };
    case 'SUCCESS':
      return { display: `${typeStr} ${major}.${minor}`, status: 'ok' };
    case 'NEED_UPDATE':
      return { display: `${typeStr} ${major}.${minor} (Needs Update)`, status: 'warning' };
    case 'UNKNOWN':
    default:
      return { display: resultStr, status: 'unknown' };
  }
}

/**
 * Get CSS classes for ESC display based on type and status
 * Color logic:
 * - BLHELI = red (we want to phase out)
 * - AM32 needing update = amber/yellow
 * - AM32 up to date = green (only green option)
 * - Error states = red
 * - Unknown = gray
 *
 * @param {string} display - The display string from parseESCVersion (e.g., "AM32 1.5", "BLHELI 32.9")
 * @param {string} status - 'ok', 'warning', 'error', or 'unknown'
 * @returns {string} - Tailwind CSS classes
 */
export function getESCStatusClasses(display, status) {
  // Error states
  if (status === 'error') return 'text-red-400 font-semibold';

  // BLHELI is always red (phasing out)
  if (display && display.startsWith('BLHELI')) return 'text-red-400';

  // AM32 - check if needs update
  if (display && display.startsWith('AM32')) {
    if (display.includes('Needs Update')) {
      return 'text-yellow-400';
    }
    return 'text-green-400'; // Good to go
  }

  // Unknown/other
  return 'text-on-surface-variant';
}

/**
 * Get ESC type and version as a simple string for filtering
 * Returns just the type + version without status info
 * @param {number|null} result - ESC version result (0-5)
 * @param {number|null} type - ESC type (0-4)
 * @param {number|null} major - Major version number
 * @param {number|null} minor - Minor version number
 * @returns {string|null} - e.g., "AM32 1.5" or null if no valid data
 */
export function getESCFilterKey(result, type, major, minor) {
  // Handle null/undefined/default values
  if (result === null || result === undefined || result === 255) {
    return null;
  }

  const resultStr = ESC_RESULTS[result] ?? 'UNKNOWN';
  const typeStr = ESC_TYPES[type] ?? 'UNKNOWN';

  // Only return a filter key for SUCCESS or NEED_UPDATE (versions we can identify)
  if (resultStr === 'SUCCESS' || resultStr === 'NEED_UPDATE') {
    return `${typeStr} ${major}.${minor}`;
  }

  // For error states, return the status as the key
  if (resultStr === 'NO_DATA' || resultStr === 'MISMATCH' || resultStr === 'DAMAGED') {
    return resultStr === 'NO_DATA' ? 'No Data' :
           resultStr === 'MISMATCH' ? 'Mismatch' : 'Damaged';
  }

  return null;
}

/**
 * Format firmware version for display
 * Shows label if available, otherwise truncated hash with full hash on hover
 * @param {string|null} hash - Git hash
 * @param {string|null} label - Human-readable label (from firmware_labels table)
 * @returns {Object} - { display: string, title: string|null }
 */
export function formatFirmwareVersion(hash, label) {
  if (!hash) {
    return { display: '-', title: null };
  }

  if (label) {
    return { display: label, title: hash };
  }

  // Truncate hash to 7 characters (standard git short hash)
  const shortHash = hash.substring(0, 7);
  return { display: shortHash, title: hash };
}
